import { PRICING_2026_OVERRIDES, isRepricedPlanId } from './pricing-2026-values';
import { ChargeBeePlan, ChargeBeePlanId } from './types';
import { getPlanById } from './utils';

/** The instant the September 2026 self-serve list prices take effect (inclusive). */
export const PRICING_2026_CUTOVER = new Date('2026-09-15T00:00:00Z');

/** Which generation of list prices a subscription is billed at. */
export enum PricingGeneration {
  G2025 = 'g2025',
  G2026 = 'g2026',
}

/**
 * What a subscription's pricing decision needs to know about it: which plan it
 * is on, and when its current billing period started.
 */
export type SubscriptionPricingContext = {
  readonly chargebeePlanId: string;
  /**
   * Optional and nullable on purpose: `users.last_charged` / `session_users.last_charged`
   * are declared `last_charged?: Date` in db-client, so a record straight from the
   * database omits the key entirely. Both spellings mean "never charged".
   */
  readonly lastCharged?: Date | null;
  /**
   * Optional and nullable for the same reason as `lastCharged`: a subscription
   * can reach the pricing decision with neither date recorded (the billing
   * webhook's own fallback chain has always allowed for it), and a repricing
   * must never be the thing that throws inside an invoice run.
   */
  readonly signupDate?: Date | null;
  readonly customPrice?: number;
};

/**
 * Which generation of list prices a billing period that starts at this instant is charged at.
 *
 * Deliberately time-free: the answer depends only on the billing period itself, never on
 * "now", so the same instant always resolves the same way.
 */
export const generationOfBillingPeriodStart = (billingPeriodStart?: Date | null): PricingGeneration =>
  // No recorded billing period start means nothing says this subscription has renewed into the new
  // prices, so it keeps the ones it has. Repricing only ever happens on positive evidence.
  billingPeriodStart && billingPeriodStart.getTime() >= PRICING_2026_CUTOVER.getTime()
    ? PricingGeneration.G2026
    : PricingGeneration.G2025;

/** Resolves the plan a subscription is actually priced at. */
export const resolvePlanForSubscription = (subscription: SubscriptionPricingContext): ChargeBeePlan => {
  const plan = getPlanById(subscription.chargebeePlanId);

  // A negotiated custom price is fixed by the deal, so the repricing never touches it.
  // `Number.isFinite` on purpose: a custom price of 0 is a real negotiated value.
  if (Number.isFinite(subscription.customPrice)) {
    return plan;
  }

  const billingPeriodStart = subscription.lastCharged ?? subscription.signupDate;

  if (generationOfBillingPeriodStart(billingPeriodStart) === PricingGeneration.G2025) {
    return plan;
  }

  // Only the three self-serve plans carry a 2026 override; every other plan id
  // keeps the pricing it already has.
  const override = isRepricedPlanId(plan.chargeBeePlanId) ? PRICING_2026_OVERRIDES[plan.chargeBeePlanId] : undefined;

  return override ? { ...plan, ...override } : plan;
};

/**
 * Which pricing generation's list price `amountInCents` is for this plan, or `undefined`
 * when it is no generation's list price. Only the three repriced plans can answer: every
 * other plan charges the same amount either side of the cutover, so its list price singles
 * out neither generation.
 */
export const listPriceGenerationOf = (
  chargebeePlanId: string,
  amountInCents: number
): PricingGeneration | undefined => {
  const plan = getPlanById(chargebeePlanId);

  if (!isRepricedPlanId(plan.chargeBeePlanId)) {
    return undefined;
  }

  if (plan.costInCents === amountInCents) {
    return PricingGeneration.G2025;
  }

  return PRICING_2026_OVERRIDES[plan.chargeBeePlanId].costInCents === amountInCents
    ? PricingGeneration.G2026
    : undefined;
};

/**
 * Is `amountInCents` what the catalog charges for this plan in any of its pricing
 * generations? Only the three repriced plans have two; every other plan has one.
 *
 * Generation-blind on purpose. An amount does name its own generation, but during the
 * stagger window that name carries no verdict: a mid-term plan change is priced from the new
 * catalog while its billing period still starts before the cutover, and a subscription the
 * cutover pass has not reached yet reports the old amount with a billing period after it.
 * Both are catalog-priced, and neither may be recorded as a negotiated deal.
 */
export const isListPriceOfAnyGeneration = (chargebeePlanId: string, amountInCents: number): boolean =>
  getPlanById(chargebeePlanId).costInCents === amountInCents ||
  listPriceGenerationOf(chargebeePlanId, amountInCents) !== undefined;

/** What a subscription resuming from cancellation needs to know to be priced. */
export type ResumedSubscriptionPricingContext = {
  readonly chargebeePlanId: string;
  /** What ChargeBee currently holds for this subscription, in cents. */
  readonly currentAmountInCents?: number;
  readonly customPrice?: number;
  /** When the resumed subscription's new billing period starts. */
  readonly resumedAt: Date;
};

/**
 * The catalog price a subscription resuming from cancellation should be charged, or `undefined`
 * when its price is not the catalog's to set.
 *
 * ChargeBee resumes a subscription at the price frozen on it when it was cancelled unless it is
 * given a new one, so without this an account cancelled before a repricing comes back on the old
 * prices for good (PRE-3578).
 *
 * `undefined` means "leave whatever ChargeBee holds": `resolvePlanForSubscription` answers with the
 * catalog plan for a negotiated account rather than that account's own amount, so returning a price
 * unconditionally would overwrite a comped account with the list price.
 */
export const catalogPriceForResumedSubscription = (
  subscription: ResumedSubscriptionPricingContext
): number | undefined => {
  const hasNegotiatedPrice = Number.isFinite(subscription.customPrice);
  const isOutsideRepricingScope = !isRepricedPlanId(subscription.chargebeePlanId as ChargeBeePlanId);

  // Ordered, not combined: `isListPriceOfAnyGeneration` reads the catalog through `getPlanById`,
  // which throws for a plan id the catalog does not list. Reactivation must stay possible for an
  // account on a one-off plan created straight in ChargeBee, so the scope check — a pure id
  // comparison — has to answer first.
  if (hasNegotiatedPrice || isOutsideRepricingScope) {
    return undefined;
  }

  const isPricedByHand = !isListPriceOfAnyGeneration(
    subscription.chargebeePlanId,
    subscription.currentAmountInCents as number
  );

  if (isPricedByHand) {
    return undefined;
  }

  return resolvePlanForSubscription({
    chargebeePlanId: subscription.chargebeePlanId,
    lastCharged: subscription.resumedAt,
  }).costInCents;
};
